/**
 * Manufacturer Trade Show — annual event (every 360 in-game days).
 *
 * Every January-ish (totalDays % 360 === 0, after day 0), the player is
 * invited to a transport trade show. For each manufacturer where they hold
 * at least the "Client régulier" tier (units ≥ 1), the brand presents two
 * offers:
 *
 *   1. DEMO 30J : a vehicle of a tier ABOVE what the player currently owns
 *                 (or tier+1 at most). Added to the fleet for 30 days with
 *                 isDemo=true; auto-removed when totalDays >= demoExpiresOn.
 *                 No money exchanged.
 *
 *   2. COMMANDE GROUPÉE : a bundle of 3 vehicles of a chosen model at -8%
 *                 on the catalogue price. Charged immediately to treasury.
 *
 * The show modal pops once per anniversary. The player can accept ONE
 * offer per brand (demo OR bundle) or skip the booth.
 */
import { VEHICLES } from './vehicles';
import { MANUFACTURER_FINANCE, getManufacturerLoyaltyTier } from './manufacturerFinance';

export const BUNDLE_DISCOUNT = 0.08;
export const BUNDLE_SIZE = 3;
export const DEMO_DAYS = 30;

/**
 * Build the trade show offer list from the current game state.
 * Returns null when no brand qualifies (player has 0 manufacturer relations).
 *
 * @param {object} state - the game store
 * @returns {{year, brandOffers, totalDays} | null}
 */
export function generateTradeShowOffers(state) {
  const relations = state.manufacturerRelations || {};
  const fleet = state.vehicles || [];

  const offers = [];
  for (const brandId of Object.keys(MANUFACTURER_FINANCE)) {
    const rel = relations[brandId];
    if (!rel || (rel.units || 0) < 1 || rel.defaulted) continue;

    const mfg = MANUFACTURER_FINANCE[brandId];
    const brandFleet = fleet.filter(v => v.brand === brandId);
    const maxOwnedTier = brandFleet.length > 0
      ? Math.max(...brandFleet.map(v => v.tier || 1))
      : 1;
    const demoTier = Math.min(4, maxOwnedTier + 1);
    const candidates = VEHICLES.filter(v => v.brand === brandId && v.tier === demoTier);
    const demoModel = candidates[0] || VEHICLES.filter(v => v.brand === brandId).slice(-1)[0];
    if (!demoModel) continue;
    const bundleCandidates = VEHICLES.filter(v => v.brand === brandId && v.tier <= maxOwnedTier);
    const bundleModel = bundleCandidates[0] || VEHICLES.filter(v => v.brand === brandId)[0];
    if (!bundleModel) continue;

    const tier = getManufacturerLoyaltyTier(brandId, rel.units || 0);
    offers.push({
      brandId,
      brandName: mfg.name,
      brandColor: mfg.color,
      tagline: mfg.tagline,
      loyaltyLabel: tier?.label || 'Prospect',
      units: rel.units || 0,
      demo: {
        vehicleDataId: demoModel.id,
        label: `${demoModel.brand} ${demoModel.model}`,
        tier: demoModel.tier,
        image: demoModel.image || null,
        days: DEMO_DAYS,
      },
      bundle: {
        vehicleDataId: bundleModel.id,
        label: `${bundleModel.brand} ${bundleModel.model}`,
        tier: bundleModel.tier,
        image: bundleModel.image || null,
        unitPrice: bundleModel.price,
        size: BUNDLE_SIZE,
        discount: BUNDLE_DISCOUNT,
        totalListPrice: bundleModel.price * BUNDLE_SIZE,
        totalDiscountedPrice: Math.round(bundleModel.price * BUNDLE_SIZE * (1 - BUNDLE_DISCOUNT)),
      },
    });
  }

  if (offers.length === 0) return null;
  return {
    active: true,
    year: state.year || 1,
    totalDays: state.totalDays,
    brandOffers: offers,
    decisions: {}, // brandId → 'demo'|'bundle'|'skip'
  };
}
